import FileDownloadIcon from "@mui/icons-material/FileDownload";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import {
  Avatar,
  Box,
  Button,
  Container,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme as useMuiTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
} from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import FolderIcon from "@mui/icons-material/Folder";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import RowPerPage from "../../components/RowPerPage";
import api from "../../utilities/api";

export default function StorageList() {
  const theme = useMuiTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isDarkMode = theme.palette.mode === "dark";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  // File Viewer State
  const [openFilesDialog, setOpenFilesDialog] = useState(false);
  const [userFiles, setUserFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [returnedCount, setReturnedCount] = useState(0);

  // Auto-open files dialog if userId is in URL
  useEffect(() => {
    const userId = searchParams.get('userId');
    if (userId) {
      const fetchUserDataAndOpenDialog = async () => {
        try {
          setOpenFilesDialog(true);
          setLoadingFiles(true);
          
          // First fetch user info to get the name
          const userRes = await api.get(`/api/user/users/${userId}`);
          const userData = userRes.data.data;
          
          setSelectedUser({
            id: userId,
            name: `${userData.firstName} ${userData.lastName}`.trim() || userData.username || userData.email
          });

          // Then fetch files
          const filesRes = await api.get(`/api/storage/users/${userId}/files?all=true`);
          if (filesRes.data.success) {
            setUserFiles(filesRes.data.data);
            setAnalytics(filesRes.data.analytics);
            setReturnedCount(filesRes.data.returnedCount || 0);
          }
        } catch (err) {
          console.error("Failed to fetch user data for storage dialog", err);
        } finally {
          setLoadingFiles(false);
        }
      };
      fetchUserDataAndOpenDialog();
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await api.get("/api/storage/users", {
          params: {
            page: page + 1,
            limit: rowsPerPage,
            search: searchTerm,
          },
        });
        if (res.data.success) {
          setData(res.data.data);
          setTotalRows(res.data.count);
        }
      } catch (err) {
        console.error("Failed to fetch storage list", err);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(() => {
      fetchData();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm]);

  // Menu handlers
  const handleMenuOpen = (e, user) => {
    setAnchorEl(e.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleViewFiles = async () => {
    if (!selectedUser) return;

    // Close menu first
    setAnchorEl(null);
    setOpenFilesDialog(true);
    setLoadingFiles(true);
    setUserFiles([]);

    try {
      // Fetch files for the selected user (get all files without pagination)
      const res = await api.get(`/api/storage/users/${selectedUser.id}/files?all=true`);
      if (res.data.success) {
        setUserFiles(res.data.data);
        setAnalytics(res.data.analytics);
        setReturnedCount(res.data.returnedCount || 0);
      }
    } catch (err) {
      console.error("Failed to fetch user files", err);
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleCloseFilesDialog = () => {
    setOpenFilesDialog(false);
    setUserFiles([]);
    setSelectedUser(null);
    setAnalytics(null);
  };

  // Search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const totalPages = Math.ceil(totalRows / rowsPerPage);

  // Get progress bar color based on usage percentage
  const getProgressColor = (percentage) => {
    if (percentage < 30) return "#2196f3"; // blue
    if (percentage < 70) return "#2196f3"; // orange
    return "#f44336"; // red
  };

  // Render desktop table
  const renderTable = () => (
    <TableContainer
      component={Box}
      sx={{
        backgroundColor: theme.palette.background.default,
        boxShadow: "none",
        "& .MuiTableRow-root:hover": {
          backgroundColor: isDarkMode
            ? "rgba(255, 255, 255, 0.08)"
            : "rgba(0, 0, 0, 0.04)",
        },
        border: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(2),
        borderRadius: 2,
      }}
    >
      <Table>
        <TableHead>
          <TableRow>
            {[
              "User",
              "Used Storage",
              "Plan Type",
              "Plan Limit",
              "Verified",
              "Action",
            ].map((h) => (
              <TableCell
                key={h}
                sx={{
                  fontWeight: "bold",
                  color: theme.palette.primary,
                  borderBottom: `1px solid ${
                    isDarkMode
                      ? "rgba(255, 255, 255, 0.12)"
                      : "rgba(0, 0, 0, 0.12)"
                  }`,
                  padding: "16px",
                  ...(h === "Action" && { textAlign: "center" }),
                }}
              >
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : (
            data.map((u) => (
              <TableRow
                key={u.id}
                hover
                sx={{
                  borderBottom: `1px solid ${
                    isDarkMode
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.05)"
                  }`,
                }}
              >
                <TableCell sx={{ padding: "12px 16px" }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar src={u.avatar} sx={{ mr: 2 }} />
                    <Typography fontWeight="small">{u.name}</Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/users?userId=${u.id}`);
                      }}
                      sx={{ ml: 1, color: theme.palette.primary.main }}
                      title="View User Details"
                    >
                      <ArrowForwardIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: "12px 16px" }}>
                  <LinearProgress
                    variant="determinate"
                    value={u.percentage}
                    sx={{
                      width: 200,
                      height: 8,
                      borderRadius: 1,
                      backgroundColor: isDarkMode
                        ? "rgba(255, 255, 255, 0.12)"
                        : "rgba(0, 0, 0, 0.08)",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: getProgressColor(u.percentage),
                      },
                    }}
                  />
                  <Box sx={{ width: "100%", maxWidth: 200 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="body2" fontWeight="small">
                        {`${u.usedGB}GB of ${
                          u.totalGB === Infinity
                            ? "Unlimited"
                            : `${u.totalGB}GB`
                        } used`}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: "12px 16px" }}>
                  {u.planType}
                </TableCell>
                <TableCell sx={{ padding: "12px 16px" }}>
                  {u.planLimit}
                </TableCell>
                <TableCell sx={{ padding: "12px 16px" }}>
                  {u.isVerified ? "Yes" : "No"}
                </TableCell>
                <TableCell
                  sx={{
                    padding: "12px 16px",
                    textAlign: "center",
                    width: "48px",
                  }}
                >
                  <IconButton
                    onClick={(e) => handleMenuOpen(e, u)}
                    size="small"
                    sx={{
                      color: theme.palette.text.secondary,
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Container
      // maxWidth="xl"
      maxWidth="100%"
      sx={{
        // py: 4,
        bgcolor: theme.palette.background.default,
        color: theme.palette.text.primary,
        boxShadow: "none",
        border: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(2),
        borderRadius: 2,
      }}
    >
      {/* Header with search and export */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="h4" fontWeight="bold" sx={{ mr: 3 }}>
            Storage List
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <RowPerPage
              rowsPerPage={rowsPerPage}
              onRowsChange={(n) => {
                setRowsPerPage(n);
                setPage(0);
              }}
              options={[6, 10, 20, 30]}
            />
          </Box>
        </Box>

        {/* Search and Export Buttons */}
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              bgcolor: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "#f5f5f5",
              borderRadius: 1,
              py: 0.5,
              px: 2,
            }}
          >
            <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
            <TextField
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearch}
              variant="standard"
              InputProps={{
                disableUnderline: true,
              }}
              sx={{
                height: { xs: 150, sm: 18 },
                width: { xs: 150, sm: 175 },
                "& input": {
                  color: theme.palette.text.primary,
                },
              }}
            />
          </Box>

          <Button
            variant="contained"
            color="primary"
            startIcon={<FileDownloadIcon />}
            sx={{
              height: 39,
              px: 3,
              py: 1,
              bgcolor: "#1976d2",
              "&:hover": {
                bgcolor: "#1565c0",
              },
            }}
            onClick={async () => {
              try {
                // Trigger file download
                const response = await api.get("/api/storage/users/export", {
                  responseType: "blob", // Important for file download
                });

                // Create blob link to download
                const url = window.URL.createObjectURL(
                  new Blob([response.data])
                );
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", "storage_users_export.csv");
                document.body.appendChild(link);
                link.click();
                link.remove();
              } catch (error) {
                console.error("Export failed:", error);
              }
            }}
          >
            Export
          </Button>
        </Box>
      </Box>

      {renderTable()}

      {/* Files Dialog */}
      <Dialog
        open={openFilesDialog}
        onClose={handleCloseFilesDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Files for {selectedUser?.name} 
          {returnedCount > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              ({returnedCount} files)
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {loadingFiles ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Analytics Section */}
              {analytics && (
                <Box sx={{ mb: 3, p: 2, bgcolor: theme.palette.action.hover, borderRadius: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    Storage Analytics
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Total Files
                      </Typography>
                      <Typography variant="h6">{analytics.totalFiles}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Total Size
                      </Typography>
                      <Typography variant="h6">{analytics.totalSize}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Folders
                      </Typography>
                      <Typography variant="h6">{analytics.folderCount}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Files
                      </Typography>
                      <Typography variant="h6">{analytics.fileCount}</Typography>
                    </Box>
                    {analytics.earliestDate && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Earliest File
                        </Typography>
                        <Typography variant="body2">
                          {new Date(analytics.earliestDate).toLocaleDateString()}
                        </Typography>
                      </Box>
                    )}
                    {analytics.latestDate && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Latest File
                        </Typography>
                        <Typography variant="body2">
                          {new Date(analytics.latestDate).toLocaleDateString()}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  
                  {/* File Types Breakdown */}
                  {analytics.fileTypes && analytics.fileTypes.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Top File Types
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        {analytics.fileTypes.map((fileType, index) => (
                          <Box key={index} sx={{ 
                            bgcolor: theme.palette.background.paper,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            border: `1px solid ${theme.palette.divider}`
                          }}>
                            <Typography variant="caption">
                              {fileType.type}: {fileType.count}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {/* Files List */}
              {userFiles.length === 0 ? (
                <Typography align="center" sx={{ py: 3 }}>
                  No files found for this user.
                </Typography>
              ) : (
                <List>
                  {userFiles.map((file) => (
                    <ListItem key={file._id} divider>
                      <ListItemIcon>
                        {file.type === "folder" ? (
                          <FolderIcon color="primary" />
                        ) : (
                          <InsertDriveFileIcon color="action" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={file.name}
                        secondary={`${file.sizeFormatted} • ${new Date(
                          file.updatedAt
                        ).toLocaleDateString()}`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFilesDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pagination */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
        }}
      >
        <Typography color="text.secondary">
          Showing Rows: {page * rowsPerPage + 1}-
          {Math.min((page + 1) * rowsPerPage, totalRows)} of {totalRows}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
            sx={{ color: theme.palette.text.primary }}
          >
            <KeyboardArrowLeftIcon />
          </IconButton>

          {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => (
            <Box
              key={i}
              onClick={() => setPage(i)}
              sx={{
                mx: 0.5,
                width: 24,
                height: 24,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                bgcolor:
                  i === page ? theme.palette.primary.main : "transparent",
                color:
                  i === page
                    ? theme.palette.common.white
                    : theme.palette.text.primary,
                cursor: "pointer",
                fontWeight: i === page ? "bold" : "normal",
              }}
            >
              {i + 1}
            </Box>
          ))}

          <IconButton
            disabled={page >= totalPages - 1}
            onClick={() => setPage(page + 1)}
            sx={{ color: theme.palette.text.primary }}
          >
            <KeyboardArrowRightIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.default,
            boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.2)",
            mt: 0.5,
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={handleViewFiles} sx={{ py: 1, px: 2 }}>
          View Files
        </MenuItem>
      </Menu>
    </Container>
  );
}
